import { get, post, patch, del, postFormData } from './apiService';
import type { DishListing, ListingStatus, ImagePlaceholder } from '../types/listing';
import { getDevice, waitForDevice } from './deviceService';

export interface CreateListingRequest {
  type: 'dish';
  title: string;
  listerId: string;
  fulfillmentMethod: string[];
}

export interface PatchListingRequest {
  title?: string;
  description?: string;
  quantity?: number;
  price?: number;
  postalCode?: string;
  stateProvince?: string;
  /** Server-derived envelope. Sending it is ignored once either window below
   *  is supplied; patch the method windows instead. */
  timeWindow?: Record<string, unknown>;
  pickupWindow?: Record<string, unknown>;
  /** Send `{}` (not null/undefined) to clear it — an absent field means "leave
   *  it alone", so null can't express removal. The server also clears it on its
   *  own when `fulfillmentMethod` stops including `dropoff`. */
  dropoffWindow?: Record<string, unknown>;
  /** Miles the chef will drive; flat per-order fee in cents. The server clears
   *  both when `fulfillmentMethod` stops including `dropoff`. */
  dropoffRadius?: number | null;
  dropoffFee?: number | null;
  content?: string;
  listingLocation?: Record<string, unknown>;
  imageUrl?: string;
  thumbnailUrl?: string;
  draftBannerDismissed?: boolean;
  status?: ListingStatus;
  /**
   * Lister's stated reason for cancelling the whole listing. Required by the
   * backend whenever `status` moves to 'canceled'. Internal tracking only —
   * eaters never see it; it also gets stamped onto every order the server
   * cascade-cancels alongside the listing.
   */
  cancellationReason?: string;
  /** Per-listing audience reach (1/2/3). Stamped at publish from the chef's default. */
  shareConnectionLevel?: number;
  ingredients?: { ingredients: string[] };
  fulfillmentMethod?: string[];
  dietaryTags?: string[];
  allergenTags?: string[];
  originTags?: string[];
  spiciness?: number;
}

export async function getListings(): Promise<DishListing[]> {
  return get<DishListing[]>('/listings/mine');
}

/** My Kitchen tab keys — map to server-side dish-status filters. */
export type MyKitchenTab = 'active' | 'drafts' | 'past';

export interface GetMyKitchenListingsParams {
  status: MyKitchenTab;
  /** Page size (server caps at 100). */
  fetchSize?: number;
  /** Cursor — return listings created strictly before this listing id. */
  lastListingId?: string;
}

/**
 * Owner's listings for one My Kitchen tab, cursor-paginated newest-first. Same
 * /listings/mine endpoint as getListings, but with server-side status filtering
 * + paging (opt-in via these params). Each item carries an aggregate
 * `orderCount`.
 */
export async function getMyKitchenListings(
  params: GetMyKitchenListingsParams,
): Promise<DishListing[]> {
  const query = new URLSearchParams({ status: params.status });
  if (params.fetchSize != null) query.set('fetchSize', String(params.fetchSize));
  if (params.lastListingId) query.set('lastListingId', params.lastListingId);
  return get<DishListing[]>(`/listings/mine?${query.toString()}`);
}

export interface MyListingsCounts {
  active: number;
  drafts: number;
  past: number;
}

/** Per-tab listing counts, used to pick a sensible default My Kitchen tab. */
export async function getMyListingsCounts(): Promise<MyListingsCounts> {
  return get<MyListingsCounts>('/listings/mine/counts');
}

export interface SearchLocalListingsParams {
  localeId: string;
  lat?: number;
  lon?: number;
  page?: number;
  pageSize?: number;
  orderBy?: 'quantity' | 'price' | 'distance' | 'windowStart';
  order?: 'asc' | 'desc';
  /** Whether `lat`/`lon` are the caller's real coordinates rather than a locale
   *  centroid. Drives the drop-off out-of-range sink server-side — without it
   *  the feed would bury listings based on a guess at where the user is. */
  preciseLocation?: boolean;
}

export interface SearchLocalListingsFilters {
  dietaryInclude?: string[];
  dietaryExclude?: string[];
  allergenInclude?: string[];
  allergenExclude?: string[];
  originInclude?: string[];
  originExclude?: string[];
  spicy?: number[];
  pickupEligible?: boolean;
  /** true = drop sold-out listings from the feed. Default (omitted) shows
   *  them, sorted last. "Sold out" covers both the lister stopping orders
   *  early and the portions running out. */
  hideSoldOut?: boolean;
  startDateTime?: string;
  endDateTime?: string;
}

export interface SearchLocalListingsResponse {
  listings: DishListing[];
  page: number;
  pageSize: number;
  totalCount: number;
  // Set by the BE when the requested localeId points at a soft-deleted
  // locale whose centroid falls inside a currently-active dish locale.
  // The `listings` in this response are already for the successor locale
  // — the FE reconciles state by calling setLocale(newLocale) so future
  // reads use the new id (and the store change triggers a WS re-subscribe
  // to the new locale channel). Same visible outcome as the live
  // `locale_migrated` WS event; this is the fallback for clients that
  // missed the live broadcast (anon browsers, long-session tabs).
  localeMigrated?: string;
}

export async function searchLocalListings(
  params: SearchLocalListingsParams,
  filters: SearchLocalListingsFilters = {},
): Promise<SearchLocalListingsResponse> {
  const qs = new URLSearchParams({ locale: params.localeId });
  if (params.lat != null && params.lon != null) {
    qs.set('lat', String(params.lat));
    qs.set('lon', String(params.lon));
  }
  if (params.page != null) qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.orderBy) qs.set('orderBy', params.orderBy);
  if (params.order) qs.set('order', params.order);
  // preciseLocation rides in the body: the backend reads it alongside the
  // filters (ListingsLocalSearch), not from the query string.
  return post<SearchLocalListingsResponse>(`/listings/local/search?${qs}`, {
    ...filters,
    ...(params.preciseLocation != null ? { preciseLocation: params.preciseLocation } : {}),
  });
}

/** How long a dish open waits for the device id before going without it. */
const DEVICE_WAIT_MS = 1000;

/**
 * `anchor` is the caller's browse reference point (see `useBrowseAnchor`). It
 * is what the BE measures `distance` / `driveTime` from, and it is the SAME
 * value the feed search sends — omit it and the response simply carries no
 * distance. It used to be omitted here, and the BE quietly fell back to the
 * caller's saved profile address, which is why one dish could show two
 * different distances on the feed and on its detail page.
 */
export async function getListingDetail(
  id: string,
  anchor?: { lat: number; lon: number } | null,
  opts: { trackView?: boolean; quiet404?: boolean } = {},
): Promise<DishListing> {
  const qs = new URLSearchParams();
  if (anchor != null) {
    qs.set('lat', String(anchor.lat));
    qs.set('lon', String(anchor.lon));
  }
  // `trackView` is the explicit "someone opened this dish" signal: the BE
  // records a listing view ONLY when deviceId is present, so every other fetch
  // of a listing (status toasts, the edit page, activity cards) is never
  // counted. Signed out, the view keys on this stable per-browser id.
  // The wait is bounded: waitForDevice never resolves if device collection
  // failed, and that must not hold up the page (the open just isn't counted).
  if (opts.trackView) {
    await Promise.race([waitForDevice(), new Promise<void>((resolve) => setTimeout(resolve, DEVICE_WAIT_MS))]);
    const deviceId = getDevice()?.identifier;
    if (deviceId) qs.set('deviceId', deviceId);
  }
  const query = qs.toString();
  return get<DishListing>(query ? `/listings/${id}?${query}` : `/listings/${id}`, undefined, {
    quiet404: opts.quiet404,
  });
}

export async function createListing(body: CreateListingRequest): Promise<DishListing> {
  return post<DishListing>('/listings', body);
}

export async function patchListing(
  id: string,
  body: PatchListingRequest,
  opts: { quietStaleFetch?: boolean } = {},
): Promise<DishListing> {
  return patch<DishListing>(`/listings/${id}`, body, undefined, {
    quietStaleFetch: opts.quietStaleFetch,
  });
}

export async function deleteListing(id: string): Promise<void> {
  return del<void>(`/listings/${id}`);
}

export async function uploadListingImage(listingId: string, file: File): Promise<DishListing> {
  const formData = new FormData();
  formData.append('listingId', listingId);
  formData.append('file', file);
  return postFormData<DishListing>('/listings/image', formData);
}

export interface CreateShareLinkRequest {
  route: string;
}

export interface ShareLinkResponse {
  ref: string;
  /**
   * Public CDN URL of a branded QR PNG. Populated only when `qr` was requested
   * (app shares only — the backend rejects `qr` alongside a listingId).
   */
  qrUrl?: string;
}

export interface ResolvedShareLink {
  route: string;
  listingId?: string;
  sharedBy: {
    id: string;
    displayName: string;
    avatarUrl: string;
  };
  /**
   * Present when an authenticated (already-registered) caller resolved an
   * app-share link and now has a still-pending scan-to-connect request from the
   * sharer. The caller is routed to the `/c={id}` accept/decline card. Absent
   * for anonymous callers, self-shares, and already-connected pairs.
   */
  pendingConnectionId?: string;
}

export interface GenerateListingRequest {
  listingId?: string;
  title?: string;
  description?: string;
  fields: string[];
}

export interface GenerateListingResponse {
  listingId: string;
  description?: string;
  ingredientsItems?: string[];
  tags?: {
    dietary: string[] | null;
    allergens: string[] | null;
    allergen_reasons: Record<string, string> | null;
    origins: string[] | null;
    spicy: number;
  };
  image?: {
    placeholder_title: string;
    public_url: string;
  };
}

export async function generateListing(
  body: GenerateListingRequest,
  signal?: AbortSignal,
  serviceKey?: string,
): Promise<GenerateListingResponse> {
  // serviceKey is set for logged-out callers (the `listing_generate`-scoped web
  // key) so the backend runs its stateless path; authenticated callers omit it
  // and generation uses their session.
  return post<GenerateListingResponse>('/listings/generate', body, serviceKey, signal);
}

export async function createShareLink(
  listingId: string | null | undefined,
  body: CreateShareLinkRequest,
  qr?: 'primary' | 'secondary',
): Promise<ShareLinkResponse> {
  // `qr` is only valid on app shares (no listingId); the backend returns a
  // branded QR PNG url in the response when it's present.
  const params = new URLSearchParams();
  if (listingId) params.set('listingId', listingId);
  if (qr) params.set('qr', qr);
  const query = params.toString();
  const path = query ? `/share?${query}` : '/share';
  return post<ShareLinkResponse>(path, body);
}

export async function resolveShareLink(ref: string): Promise<ResolvedShareLink> {
  return get<ResolvedShareLink>(`/share?ref=${encodeURIComponent(ref)}`);
}

export async function getImagePlaceholders(
  searchText?: string,
  pageSize = 1000,
  serviceKey?: string,
): Promise<ImagePlaceholder[]> {
  const params = new URLSearchParams({ pageSize: String(pageSize) });
  if (searchText) params.set('searchText', searchText);
  // serviceKey lets a logged-out caller (the web `listing_generate` key) browse
  // the preset catalog; authed callers omit it and use their session.
  return get<ImagePlaceholder[]>(`/listings/image-placeholders?${params}`, serviceKey);
}
